import React, { useContext, useEffect, useMemo, useState } from 'react';

import { CombatContext } from '../contexts/CombatContext';
import SpellTargetSelector, { effectiveSpellTargets } from '../panels/spells/SpellTargetSelector';
import { ActionPill, StatusBadge, WorkspaceCard, useLayeredModal } from './ui/LayeredWorkspace';
import { ensureSharingContext } from './sharingApi';
import { runtimeContextPayload } from './combatContextIdentity';
import {
  activateInventoryItem, fetchInventory, transferInventoryCurrency,
  transitionInventoryItem,
} from './inventoryApi';

const TERMINAL = ['CONSUMED', 'SOLD', 'TRANSFORMED', 'DESTROYED'];
const ACTION_EFFECTS = ['damage', 'healing', 'condition', 'triggered_effect'];

function categoryOf(item) {
  if (item.state === 'EQUIPPED') return 'Equipped';
  const map = {
    WEAPON: 'Weapons', ARMOUR: 'Armour', MAGIC_ITEM: 'Magic Items',
    CONSUMABLE: 'Consumables', AMMUNITION: 'Ammunition', TOOL: 'Tools',
    MATERIAL: 'Materials', TREASURE: 'Treasure / Valuables', QUEST_ITEM: 'Quest Items',
  };
  return item.state === 'STORED' ? 'Stored items' : (map[item.item_type] || 'Other items');
}

function actionable(item) {
  const effect = item.effect || {};
  return ACTION_EFFECTS.some(function (key) { return effect[key] !== undefined && effect[key] !== null; });
}

function ItemDetail({ item }) {
  const fields = [
    ['Type', item.item_type], ['State', item.state], ['Quantity', item.quantity],
    ['Charges', item.charges == null ? 'Not charge-based' : (item.charges + ' / ' + item.max_charges)],
    ['Quality', item.data && item.data.quality], ['Material', item.data && item.data.material],
    ['Weight', item.data && item.data.weight], ['Value', item.data && item.data.value],
    ['Requirements', item.data && item.data.requirements], ['Restrictions', item.data && item.data.restrictions],
  ];
  return <div className="cp-item-detail"><WorkspaceCard title={item.name} type={item.item_type} source={item.provenance && item.provenance.source_type} status={item.state} summary={(item.data && item.data.description) || 'Canonical Combat PRO item'} />
    <div className="cp-item-detail-grid">{fields.filter(function (row) { return row[1] !== undefined && row[1] !== null && row[1] !== ''; }).map(function (row) { return <div key={row[0]}><span>{row[0]}</span><b>{String(row[1])}</b></div>; })}</div>
    <section><h3>Combat properties</h3><pre>{JSON.stringify(item.effect || {}, null, 2)}</pre></section>
    <section><h3>Provenance</h3><pre>{JSON.stringify(item.provenance || {}, null, 2)}</pre></section>
    <section><h3>History</h3>{(item.history || []).map(function (event, index) { return <div key={index}>{event.event} · version {event.version} · {event.at}</div>; })}</section>
  </div>;
}

export function PlayerInventoryWorkspace({ playerId }) {
  const { state, applyPlayerEquipmentState, applyPlayerItemEffect, spellTargetKey } = useContext(CombatContext);
  const { openLayer } = useLayeredModal();
  const player = state.players.find(function (candidate) { return candidate.id === playerId; });
  const [identity, setIdentity] = useState(null);
  const [inventory, setInventory] = useState({ items: [], currency_accounts: [], derived_equipment: {} });
  const [selectedId, setSelectedId] = useState(null);
  const [notice, setNotice] = useState('Connecting canonical inventory…');
  const [results, setResults] = useState([]);
  const [sourceWallet, setSourceWallet] = useState('');
  const [destinationWallet, setDestinationWallet] = useState('');
  const [amount, setAmount] = useState('');

  async function refresh(context) {
    const canonical = (context.players || []).find(function (entry) { return String(entry.runtime_ref) === String(playerId); });
    if (!canonical) { throw new Error('player identity missing'); }
    const data = await fetchInventory(context.campaign_id, canonical.player_id);
    setIdentity({ campaign_id: context.campaign_id, player_id: canonical.player_id });
    setInventory(data);
    applyPlayerEquipmentState(playerId, data.derived_equipment || {});
    setNotice('Persistent inventory connected.');
  }

  useEffect(function () {
    let live = true;
    ensureSharingContext(runtimeContextPayload(state)).then(function (context) {
      if (!live) return null;
      return refresh(context);
    }).catch(function () { if (live) setNotice('Inventory could not connect. No local duplicate was created.'); });
    return function () { live = false; };
    // Identity bootstrap runs once per modal; encounter state remains owned by CombatContext.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selected = inventory.items.find(function (item) { return item.id === selectedId; }) || null;
  const targetIndex = selected ? inventory.items.findIndex(function (item) { return item.id === selected.id; }) : 0;
  const targetCaster = 'item:' + (selected ? selected.id : 'none');
  const targetKey = spellTargetKey(targetCaster, targetIndex);
  const autoTargets = player ? ['p:' + player.id] : [];
  const targets = effectiveSpellTargets(state, targetKey, autoTargets);
  const categories = useMemo(function () {
    const value = {};
    inventory.items.filter(function (item) { return TERMINAL.indexOf(item.state) < 0; }).forEach(function (item) {
      const category = categoryOf(item);
      if (!value[category]) value[category] = [];
      value[category].push(item);
    });
    return value;
  }, [inventory.items]);

  async function move(item, nextState) {
    if (!identity) { setNotice('Inventory identity is still connecting.'); return; }
    try {
      const data = await transitionInventoryItem(item.id, {
        state: nextState, expected_version: item.version,
        player_id: ['PARTY', 'UNCLAIMED', 'FOUND'].indexOf(nextState) >= 0 ? null : identity.player_id,
      });
      setInventory(function (current) { return Object.assign({}, current, {
        items: current.items.map(function (row) { return row.id === item.id ? data.item : row; }),
        derived_equipment: data.derived_equipment,
      }); });
      applyPlayerEquipmentState(playerId, data.derived_equipment || {});
      setNotice(item.name + ' moved to ' + nextState + '.');
    } catch (_error) { setNotice('Item move rejected; refresh before retrying.'); }
  }

  async function activateItem(item) {
    if (!identity) { setNotice('Inventory identity is still connecting.'); return; }
    try {
      const data = await activateInventoryItem(item.id, { action: item.item_type === 'CONSUMABLE' ? 'USE' : 'ACTIVATE', expected_version: item.version, targets: targets });
      const actual = applyPlayerItemEffect(data.effect, targets);
      setResults(actual);
      setInventory(function (current) { return Object.assign({}, current, { items: current.items.map(function (row) { return row.id === item.id ? data.item : row; }) }); });
      setNotice(item.name + ' resolved through Combat PRO.');
    } catch (_error) { setNotice('Item action rejected; charges and quantity were not changed by this client.'); }
  }

  async function transferMoney() {
    if (!identity) { setNotice('Inventory identity is still connecting.'); return; }
    const source = inventory.currency_accounts.find(function (wallet) { return wallet.id === sourceWallet; });
    if (!source || !destinationWallet || !Number(amount)) return;
    try {
      const data = await transferInventoryCurrency({
        source_account_id: sourceWallet, destination_account_id: destinationWallet,
        amount_minor: Number(amount), expected_source_version: source.version,
        idempotency_key: 'cpui_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8),
        reason: 'Combat PRO explicit inventory transfer',
      });
      setInventory(function (current) { return Object.assign({}, current, { currency_accounts: current.currency_accounts.map(function (wallet) {
        if (wallet.id === data.source.id) return data.source;
        if (wallet.id === data.destination.id) return data.destination;
        return wallet;
      }) }); });
      setAmount(''); setNotice('Currency transferred with value conserved.');
    } catch (_error) { setNotice('Currency transfer rejected.'); }
  }

  if (!player) return <p>Character is no longer in this encounter.</p>;
  return <div className="cp-player-inventory">
    <div className="cp-player-spells-head"><div><span className="cp-layer-eyebrow">Items / Equipment</span><h2>{player.name}</h2></div><StatusBadge tone={identity ? 'ready' : 'pending'}>{identity ? 'PERSISTENT' : 'CONNECTING'}</StatusBadge></div>
    <p role="status" className="cp-visibility-notice">{notice}</p>
    <div className="cp-inventory-layout"><div className="cp-inventory-groups">
      {Object.keys(categories).length ? Object.keys(categories).map(function (category) { return <section key={category}><h3>{category}</h3><div className="cp-inventory-cards">{categories[category].map(function (item) { return <div key={item.id} className={'cp-inventory-card' + (selectedId === item.id ? ' is-selected' : '')}>
        <button type="button" onClick={function () { setSelectedId(item.id); setResults([]); }}><b>{item.name}</b><span>{item.quantity > 1 ? ('×' + item.quantity) : item.state}</span><small>{item.charges == null ? item.item_type : (item.charges + '/' + item.max_charges + ' charges')}</small></button>
        <div className="cp-action-row">
          <ActionPill onClick={function () { openLayer({ title: item.name, eyebrow: item.item_type, subtitle: 'Canonical item detail, provenance and history.', content: <ItemDetail item={item} /> }); }}>Details</ActionPill>
          {item.state === 'EQUIPPED' ? <ActionPill onClick={function () { move(item, 'CARRIED'); }}>Unequip</ActionPill> : null}
          {item.state !== 'EQUIPPED' && ['WEAPON', 'ARMOUR', 'MAGIC_ITEM', 'TOOL'].indexOf(item.item_type) >= 0 ? <ActionPill onClick={function () { move(item, 'EQUIPPED'); }}>Equip</ActionPill> : null}
          {item.state === 'PARTY' ? <ActionPill onClick={function () { move(item, 'PERSONAL'); }}>Take from Party</ActionPill> : <ActionPill onClick={function () { move(item, 'PARTY'); }}>Add to Party</ActionPill>}
          {item.state !== 'STORED' && item.state !== 'PARTY' ? <ActionPill onClick={function () { move(item, 'STORED'); }}>Store</ActionPill> : null}
        </div>
      </div>; })}</div></section>; }) : <div className="vi-empty">No canonical items in this inventory yet.</div>}
    </div><aside className="cp-inventory-action">
      {selected && actionable(selected) ? <><h3>{selected.name}</h3><SpellTargetSelector casterKey={targetCaster} spellIdx={targetIndex} autoTargets={autoTargets} selfTarget={'p:' + player.id} allowGroups={true} /><ActionPill primary onClick={function () { activateItem(selected); }}>{selected.item_type === 'CONSUMABLE' ? 'USE' : 'ACTIVATE'}</ActionPill></> : <p>Select an actionable item to use it. Passive equipment applies automatically while equipped.</p>}
      {results.length ? <div className="cp-player-spell-result">{results.map(function (result, index) { return <div key={index}><b>{result.target || result.token}</b>: {result.state}{result.damage ? (' · ' + result.damage + ' damage') : ''}{result.healing ? (' · ' + result.healing + ' healing') : ''}{result.condition ? (' · ' + result.condition) : ''}</div>; })}</div> : null}
    </aside></div>
    <section className="cp-inventory-currency"><h3>Currency and Party Treasury</h3><div className="cp-wallet-cards">{inventory.currency_accounts.map(function (wallet) { return <div key={wallet.id}><b>{wallet.account_kind}</b><span>{wallet.balance_minor} {wallet.currency_code}</span></div>; })}</div>
      <div className="cp-currency-transfer"><label>From<select aria-label="Currency source" value={sourceWallet} onChange={function (event) { setSourceWallet(event.target.value); }}><option value="">Choose explicit source</option>{inventory.currency_accounts.map(function (wallet) { return <option key={wallet.id} value={wallet.id}>{wallet.account_kind} · {wallet.currency_code}</option>; })}</select></label><label>To<select aria-label="Currency destination" value={destinationWallet} onChange={function (event) { setDestinationWallet(event.target.value); }}><option value="">Choose destination</option>{inventory.currency_accounts.filter(function (wallet) { return wallet.id !== sourceWallet; }).map(function (wallet) { return <option key={wallet.id} value={wallet.id}>{wallet.account_kind} · {wallet.currency_code}</option>; })}</select></label><label>Amount<input aria-label="Currency amount" type="number" min="1" value={amount} onChange={function (event) { setAmount(event.target.value); }} /></label><ActionPill primary onClick={transferMoney}>Transfer</ActionPill></div>
    </section>
  </div>;
}

export default function PlayerInventoryChip({ playerId, label }) {
  const { state } = useContext(CombatContext);
  const { openLayer } = useLayeredModal();
  const player = state.players.find(function (candidate) { return candidate.id === playerId; });
  if (!player || player.companion) return null;
  return <button type="button" className="cp-action-pill cp-player-inventory-chip" onClick={function () { openLayer({ title: 'Items / Equipment', eyebrow: player.name, subtitle: 'Persistent personal inventory, equipment, currency and party treasury.', content: <PlayerInventoryWorkspace playerId={player.id} /> }); }}>{label || 'Inventory'}</button>;
}
